// ADD EXPENSE, RECEIPT FIRST (build pack 26/10/2026, pages 10–17; Karan's
// decisions the same day). One dialog for every purchase claim — Time ›
// Expenses, a timesheet row, Procurement:
//
//   - The receipt is dropped (or photographed) on the left. The reader runs at
//     once — no Read button — and fills the form, each field tagged From the
//     receipt (or Check). It never saves: a person checks and presses Add expense.
//   - Total paid is what you enter or what is read (Karan 26/10: the TOTAL,
//     reversing 14/07's net + rate). The code works out VAT and net
//     (expense-maths.js). The saved fields don't change: amount (net), vat,
//     vatRate, gross.
//   - No supplier VAT number → VAT None, saved with no VAT, and the form says why.
//   - No receipt is allowed and reaches the approver marked "No receipt".
//   - Five category buttons + the full ledger list behind More. A timesheet row
//     SUGGESTS Project Expenses; it no longer fixes it.
//   - Filing is unchanged: the ORIGINAL file goes to fileReceipt (ASP's Drive)
//     or to Storage for a contractor, exactly as before.
//   - RUPEES (Karan 01/11/2026): a £ GBP / ₹ INR switch beside Total paid, the office's
//     currency by default (my-currency.js). In rupees the VAT row is "GST (optional)" —
//     the GST the bill prints, saved as `gst`, never as UK VAT — and the receipt files to
//     the India office's folder. A receipt read in ₹ switches the form to rupees.
//
// RENDERING SAFETY. Everything the reader returns is data from a stranger's
// receipt: it is set with .value / textContent only, never as HTML.
import { addExpense, listPurchaseOrders, allProjects, callFunction, getTimesheet } from '../db.js';
import { openDialog } from './dialog.js';
import { isContractor, orgSetting } from '../org.js';
import { hasFeature } from '../features.js';
import { ownerFields, sheetKey } from '../me.js';
import { createReceiptReader } from '../receipt-model.js';
import { receiptImage, receiptKind } from '../receipt-image.js';
import { vatFromGross, planFromRead, changedFields, QUICK_CATEGORIES } from '../expense-maths.js';
import { footNote } from './mileage-block.js';
import { currencySeg } from './currency-seg.js';
import { myDefaultCurrency } from '../my-currency.js';
import { fmtMoney, gstSplit, SYMBOL, INR, readCurrency, approxGbpText } from '../expense-currency.js';
import { myFxRate } from '../my-currency.js';
import { projectPicker, loadYourProjects } from './project-picker.js';

const ICON_UP = '<svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><path d="M10 13V3.5M6 7l4-4 4 4M3.5 12.5v3a1.5 1.5 0 0 0 1.5 1.5h10a1.5 1.5 0 0 0 1.5-1.5v-3" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const ICON_CAM = '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M2 5.5A1.5 1.5 0 0 1 3.5 4h1.6l1-1.5h3.8l1 1.5h1.6A1.5 1.5 0 0 1 14 5.5v6A1.5 1.5 0 0 1 12.5 13h-9A1.5 1.5 0 0 1 2 11.5v-6Z" fill="none" stroke="currentColor" stroke-width="1.2"/><circle cx="8" cy="8.3" r="2.3" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>';
const ICON_DOC = '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M4 1.5h5L12.5 5v9.5h-8.5Z M9 1.5V5h3.5" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/></svg>';
const ICON_SPARK = '<svg viewBox="0 0 12 12" width="11" height="11" aria-hidden="true"><path d="M6 .8 7.2 4.8 11.2 6 7.2 7.2 6 11.2 4.8 7.2.8 6l4-1.2Z" fill="currentColor"/></svg>';
const ICON_WARN = '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M8 2.2 14.5 13.5h-13L8 2.2Z" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><path d="M8 6.5v3.2M8 11.3v.2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';

const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; };
const pad2 = (n) => String(n).padStart(2, '0');
const isoToday = () => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; };
const gbp = (n) => `£${Number(n).toFixed(2)}`;
const sizeLabel = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const chipLabel = (c) => c.charAt(0) + c.slice(1).toLowerCase();   // "Site Equipment" → "Site equipment" (the value stays the ledger string)
const parseMoney = (v) => { const n = Number(String(v || '').replace(/[£₹,\s]/g, '')); return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : null; };
// Some browsers give a HEIC (or a PDF from a phone's Files app) no type.
const fileType = (file) => file.type || ({ pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', heic: 'image/heic', heif: 'image/heif', webp: 'image/webp' })[(file.name.split('.').pop() || '').toLowerCase()] || '';
const fileBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1] || '');
  r.onerror = () => reject(new Error("Couldn't read the receipt file."));
  r.readAsDataURL(file);
});

/** The reader is on unless the company has switched it (or AI) off. */
export const readerEnabled = () => hasFeature('ai') && orgSetting('receiptReader', true) !== false;

/**
 * @param {object} o
 * @param {object} o.session
 * @param {object} [o.project]            { projectId, number, … } — opened from a project or a timesheet row
 * @param {string} [o.suggestedCategory]  suggested, not fixed (a timesheet row: "Project Expenses")
 * @param {string[]} o.categories         the full ledger list (EXPENSE_CATEGORIES)
 * @param {function} [o.onSaved]
 */
export function openAddExpense({ session, project = null, suggestedCategory = null, categories, onSaved, _prefill = null }) {
  const first = String(session.displayName || session.email || '').split(/[\s@]/)[0];
  const contractor = isContractor();
  // A rupee receipt files to the India office's folder (finance-folder.js location 'india').
  const filingFor = (cur) => (contractor ? 'The receipt photo is kept with the claim.' : cur === 'INR' ? 'Files itself to 01. FINANCE › ASP Chennai › 1.2 CREDITORS' : 'Files itself to 01. FINANCE › 1.2 CREDITORS');
  const filing = filingFor(_prefill && _prefill.currency);
  const st = {
    image: null,          // the shrunk JPEG for the reader + the preview
    plan: null,           // what the read filled in (planFromRead)
    readId: null,
    rate: _prefill && _prefill.rate != null ? _prefill.rate : 20,
    category: null, categoryFrom: null,   // 'read' | 'suggested' | 'person'
    whatForSuggested: false,
    tags: {},             // field → 'from' | 'check'
    projects: [], poById: {},
    currency: _prefill && _prefill.currency === 'INR' ? 'INR' : 'GBP',   // the office's by default; the person may switch
    fx: null,             // the indicative INR→GBP rate (fxRates/INR_GBP), or null: no £ shown
  };
  const money = (v) => fmtMoney(v, st.currency);

  let primaryBtn = null; let noteEl = null; let api = null;
  const n = {};            // the dialog's nodes (filled by build())
  let applied = null;      // the read already put into the form
  const reader = createReceiptReader({
    enabled: readerEnabled(),
    read: async (att) => {
      if (!att.image) throw new Error(att.imageError || 'unreadable');
      return callFunction('readReceipt', { image: att.image.base64, mimeType: 'image/jpeg', categories, currency: st.currency });
    },
    onChange: () => paint(),
  });

  const dlg = openDialog({
    title: 'Add expense',
    headAside: `${first} · paid by you, to be paid back`,
    width: 960,
    cardClass: 'tf-ax',
    note: filing,
    render: (body) => build(body),
    primary: { label: 'Add expense', onSubmit: async () => { await save(); if (onSaved) onSaved(); } },
    secondary: {
      label: 'Save and add another',
      onClick: async () => {
        await save();
        if (onSaved) onSaved();
        openAddExpense({ session, project: project && { ...project }, suggestedCategory, categories, onSaved,
          _prefill: { projectId: n.project.value() || null, poId: n.po.value || '', rate: st.rate, currency: st.currency } });
      },
    },
  });
  api = dlg.api;
  const card = dlg.card;
  primaryBtn = card.querySelector('.tf-btn-primary');
  noteEl = card.querySelector('.tf-note');
  const secondaryBtn = dlg.foot.querySelector('.tf-btn-secondary');   // the footer's, not the drop zone's Choose a file
  card.addEventListener('dragover', (e) => e.preventDefault());

  // ── The DOM ────────────────────────────────────────────────────────────────
  function box(id, { affix = null, type = 'text', placeholder = '', inputmode = null } = {}) {
    const b = el('div', 'ax-box');
    if (affix) b.appendChild(el('span', 'ax-aff', affix));
    const i = document.createElement('input');
    i.id = id; i.type = type; i.placeholder = placeholder; i.autocomplete = 'off';
    if (inputmode) i.inputMode = inputmode;
    const t = el('span', 'ax-tag'); t.hidden = true;
    const shim = el('span', 'ax-shim'); shim.setAttribute('aria-hidden', 'true');
    b.append(i, t, shim);
    return { b, i, t };
  }
  function fieldWrap(labelText, forId, { span = false, optional = false } = {}) {
    const f = el('div', `ax-f${span ? ' ax-span' : ''}`);
    const lr = el('div', 'ax-labrow');
    const l = el('label', 'tf-label', labelText); if (forId) l.htmlFor = forId;
    lr.appendChild(l);
    if (optional) lr.appendChild(el('span', 'tf-optional', 'Optional'));
    f.appendChild(lr);
    return f;
  }

  function build(body) {
    const ax = el('div', 'ax');
    // Left: the receipt.
    const left = el('div', 'ax-left');
    const drop = el('div', 'ax-drop');
    const dIco = el('span', 'ax-drop-ico'); dIco.innerHTML = ICON_UP;
    const choose = el('button', 'tf-btn tf-btn-secondary ax-choose', 'Choose a file'); choose.type = 'button';
    const fileIn = document.createElement('input'); fileIn.type = 'file'; fileIn.accept = '.pdf,image/*'; fileIn.className = 'ax-file-in'; fileIn.hidden = true;
    drop.append(dIco, el('b', 'ax-drop-title', 'Drop the receipt here'), el('span', 'ax-drop-sub', 'A PDF or a photo. Track reads it and fills in the form for you.'), choose, fileIn);
    const rec = el('div', 'ax-receipt'); rec.hidden = true;
    const imgWrap = el('div', 'ax-receipt-img');
    const img = document.createElement('img'); img.alt = 'The receipt';
    const scan = el('span', 'ax-scan'); scan.setAttribute('aria-hidden', 'true');
    imgWrap.append(img, scan);
    const fl = el('div', 'ax-fileline');
    const fIco = el('span', 'ax-fileico'); fIco.innerHTML = ICON_DOC;
    const fName = el('b', 'ax-fname'); const fSize = el('span', 'ax-fsize');
    const fSpin = el('span', 'mi-spin'); fSpin.setAttribute('aria-hidden', 'true');
    const replace = el('button', 'mi-link ax-replace', 'Replace'); replace.type = 'button';
    fl.append(fIco, fName, fSize, fSpin, replace);
    // Phone: what was read, beside the thumbnail (pack p15).
    const sum = el('div', 'ax-sum');
    const sumTag = el('span', 'ax-tag ax-tag-from', 'Read');
    const sumTotal = el('div', 'ax-sum-total'); const sumSup = el('div', 'ax-sum-sup');
    const retake = el('button', 'mi-link ax-retake', 'Retake'); retake.type = 'button';
    sum.append(sumTag, sumTotal, sumSup, retake);
    rec.append(imgWrap, sum, fl);
    const lf = el('div', 'ax-left-foot');
    const photo = el('label', 'ax-photo');
    const cIco = el('span', 'ax-cam'); cIco.innerHTML = ICON_CAM;
    const photoIn = document.createElement('input'); photoIn.type = 'file'; photoIn.accept = 'image/*'; photoIn.setAttribute('capture', 'environment'); photoIn.className = 'ax-photo-in';
    photo.append(cIco, el('span', 'ax-photo-desk', 'On your phone, take a photo instead'), el('span', 'ax-photo-phone', 'Take a photo of the receipt'), photoIn);
    const noRec = el('button', 'mi-link ax-noreceipt', 'No receipt'); noRec.type = 'button';
    lf.append(photo, noRec);
    left.append(drop, rec, lf);

    // Right: the form.
    const right = el('div', 'ax-right');
    const hint = el('div', 'ax-hint');
    const hIco = el('span', 'ax-hint-ico'); hIco.innerHTML = ICON_SPARK;
    const hText = el('span');
    hText.append(el('b', null, 'Drop the receipt first. '), 'Date, total, VAT and supplier fill themselves in. No receipt? Fill it in by hand; it goes to your approver marked “no receipt”.');
    hint.append(hIco, hText);
    const warn = el('div', 'ax-warn'); warn.hidden = true;
    const grid = el('div', 'ax-grid');

    const fTotal = fieldWrap('Total paid', 'ax-total');
    const total = box('ax-total', { affix: '£', placeholder: '0.00', inputmode: 'decimal' });
    total.b.classList.add('ax-money');
    const amtRow = el('div', 'ax-amtrow');
    const cur = currencySeg({ value: st.currency, onChange: (c) => { st.currency = c; paint(); } });
    // Rupees: the indicative pound figure beside the amount, live as you type ("≈ £41.20").
    const approx = el('span', 'fx-approx ax-approx');
    amtRow.append(total.b, cur.el, approx);
    fTotal.appendChild(amtRow);
    myFxRate().then((r) => { st.fx = r; paint(); }).catch(() => {});
    // The office's currency, unless the person (or a read) has set it already.
    if (!(_prefill && _prefill.currency)) myDefaultCurrency(session).then((c) => { if (!cur.touched() && !st.plan && c !== st.currency) cur.set(c); }).catch(() => {});
    const fDate = fieldWrap('Date', 'ax-date');
    const date = box('ax-date', { type: 'date' });
    date.i.max = isoToday();
    fDate.appendChild(date.b);

    const fVat = fieldWrap('VAT', null, { span: true });
    const vatRow = el('div', 'ax-vatrow');
    const seg = el('div', 'ax-seg'); seg.setAttribute('role', 'group'); seg.setAttribute('aria-label', 'VAT rate');
    [[20, '20%'], [5, '5%'], [0, 'None']].forEach(([r, t]) => { const b = el('button', null, t); b.type = 'button'; b.dataset.rate = String(r); seg.appendChild(b); });
    const split = el('span', 'ax-split');
    const vatTag = el('span', 'ax-tag'); vatTag.hidden = true;
    const vatShim = el('span', 'ax-shim ax-shim-line'); vatShim.setAttribute('aria-hidden', 'true');
    vatRow.append(seg, split, vatTag, vatShim);
    // Rupees: GST, as the bill prints it (optional) — never UK VAT.
    const gstRow = el('div', 'ax-gstrow'); gstRow.hidden = true;
    const gst = box('ax-gst', { affix: '₹', placeholder: '0.00', inputmode: 'decimal' });
    const gstSplitText = el('span', 'ax-split');
    gstRow.append(gst.b, gstSplitText);
    fVat.append(vatRow, gstRow);

    const fSup = fieldWrap('Supplier', 'ax-supplier', { span: true });
    const sup = box('ax-supplier', { placeholder: 'Who you paid' });
    fSup.appendChild(sup.b);

    const fCat = fieldWrap('Category', null, { span: true });
    const catNote = el('span', 'ax-catnote'); fCat.firstChild.appendChild(catNote);
    const chips = el('div', 'ax-chips'); chips.setAttribute('role', 'group'); chips.setAttribute('aria-label', 'Category');
    const more = document.createElement('select'); more.className = 'tf-box ax-more'; more.hidden = true; more.setAttribute('aria-label', 'All categories');
    more.appendChild(new Option('Choose a category…', ''));
    categories.forEach((c) => more.appendChild(new Option(c, c)));
    fCat.append(chips, more);

    const fWhat = fieldWrap('What it was for', 'ax-what', { span: true });
    const what = box('ax-what', { placeholder: 'What was it for?' });
    fWhat.appendChild(what.b);

    const fProj = fieldWrap('Project', 'ax-project', { optional: true });
    // The shared searchable picker (v4.6.1): your projects first, type to find any other.
    const want = (_prefill && _prefill.projectId) || (project && (project.projectId || project.id)) || null;
    const projectsP = allProjects().then((ps) => {
      st.projects = ps.filter((p) => p.number && !['Void'].includes(p.status));
      return st.projects;
    }).catch(() => []);
    const proj = projectPicker({
      id: 'ax-project', optional: true, noneLabel: 'No project', placeholder: 'Pick a project, if it’s for one',
      projects: projectsP, value: want,
      fallback: want && project ? { id: want, number: project.number || '', clientName: project.label || project.clientName || '', status: project.status || '' } : null,
      suggested: projectsP.then((ps) => loadYourProjects({ session, projects: ps, getTimesheet, sheetKey })).catch(() => []),
    });
    fProj.appendChild(proj.el);
    const fPo = fieldWrap('Purchase order', 'ax-po', { optional: true });
    const po = document.createElement('select'); po.id = 'ax-po'; po.className = 'tf-box ax-select';
    po.appendChild(new Option('No PO', ''));
    fPo.appendChild(po);

    grid.append(fTotal, fDate, fVat, fSup, fCat, fWhat, fProj, fPo);
    right.append(hint, warn, grid);
    ax.append(left, right);
    body.appendChild(ax);
    Object.assign(n, { ax, drop, fileIn, photoIn, choose, rec, img, fName, fSize, fSpin, replace, retake, sumTotal, sumSup, noRec, hint, warn,
      total, date, seg, split, vatTag, sup, chips, more, catNote, what, project: proj, po, cur, approx, fVat, vatRow, gstRow, gst, gstSplitText });

    // ── wiring ──
    choose.addEventListener('click', () => fileIn.click());
    replace.addEventListener('click', () => fileIn.click());
    retake.addEventListener('click', () => photoIn.click());
    fileIn.addEventListener('change', () => { if (fileIn.files[0]) take(fileIn.files[0]); fileIn.value = ''; });
    photoIn.addEventListener('change', () => { if (photoIn.files[0]) take(photoIn.files[0]); photoIn.value = ''; });
    ['dragenter', 'dragover'].forEach((t) => left.addEventListener(t, (e) => { e.preventDefault(); drop.classList.add('over'); }));
    ['dragleave', 'drop'].forEach((t) => left.addEventListener(t, () => drop.classList.remove('over')));
    left.addEventListener('drop', (e) => { e.preventDefault(); const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]; if (f) take(f); });
    noRec.addEventListener('click', () => {
      if (reader.view().noReceipt) { reader.clear(); st.rate = 20; } else {
        reader.noReceipt(); st.rate = 0;
        if (!date.i.value) date.i.value = isoToday();   // no receipt to read a date from: today, to change
      }
      clearRead(); paint(); total.i.focus();
    });
    seg.addEventListener('click', (e) => { const b = e.target.closest('[data-rate]'); if (!b || b.disabled) return; st.rate = Number(b.dataset.rate); untag('vat'); paint(); });
    total.i.addEventListener('input', () => { untag('total'); paint(); });
    gst.i.addEventListener('input', () => { untag('gst'); paint(); });
    date.i.addEventListener('input', () => { untag('date'); paint(); });
    sup.i.addEventListener('input', () => { untag('supplier'); paint(); });
    what.i.addEventListener('input', () => { st.whatForSuggested = false; untag('what'); paint(); });
    chips.addEventListener('click', (e) => {
      const b = e.target.closest('button'); if (!b || b.disabled) return;
      if (b.dataset.more) { more.hidden = false; more.focus(); return; }
      st.category = b.dataset.cat; st.categoryFrom = 'person'; paint();
    });
    more.addEventListener('change', () => { if (more.value) { st.category = more.value; st.categoryFrom = 'person'; more.hidden = true; paint(); } });

    // Approved POs, as before.
    const FINANCE_ROLES = ['Owner', 'Director', 'Finance'];
    const isFinance = (session.roles || []).some((r) => FINANCE_ROLES.includes(r));
    listPurchaseOrders(isFinance ? {} : { userEmail: session.email }).then((pos) => {
      pos.filter((p) => ['Approved', 'Sent'].includes(p.status)).forEach((p) => {
        st.poById[p.id] = p;
        po.appendChild(new Option(`${p.ref || p.id} — ${p.supplier || ''} £${Number(p.amount) || 0}`, p.id));
      });
      if (_prefill && _prefill.poId) po.value = _prefill.poId;
    }).catch(() => {});

    if (suggestedCategory && categories.includes(suggestedCategory)) { st.category = suggestedCategory; st.categoryFrom = 'suggested'; }
    setTimeout(() => paint(), 0);
  }

  // ── The receipt ────────────────────────────────────────────────────────────
  async function take(file) {
    if (!receiptKind(file)) { api.setNote('A receipt must be a PDF or a photo.', true); return; }
    api.setNote(filingFor(st.currency));
    clearRead();
    if (n.img.src && n.img.src.startsWith('blob:')) URL.revokeObjectURL(n.img.src);
    let image = null; let imageError = null;
    try { image = await receiptImage(file); } catch (e) { imageError = (e && e.message) || 'unreadable'; }
    st.image = image;
    reader.attach({ file, image, imageError });
  }
  function clearRead() {
    st.plan = null; st.readId = null; st.tags = {};
    if (st.categoryFrom === 'read') { st.category = null; st.categoryFrom = null; }
    if (st.whatForSuggested) { n.what.i.value = ''; st.whatForSuggested = false; }
  }
  const untag = (k) => { delete st.tags[k]; };

  /** The reader answered: fill the form. The model's text is set as .value — never HTML. */
  function applyRead(result) {
    if (applied === result) return;
    applied = result;
    // A receipt printed in ₹ (or in £) sets the currency (Karan 01/11/2026).
    const said = readCurrency(result);
    if (said && said !== st.currency) { st.currency = said; n.cur.set(said, { silent: true }); }
    const plan = planFromRead(result, { today: isoToday(), categories, currency: st.currency });
    st.plan = plan; st.readId = result && result.readId ? String(result.readId) : null;
    const tag = (k, check) => { st.tags[k] = check ? 'check' : 'from'; };
    if (plan.currency === INR) { n.gst.i.value = plan.gst != null ? plan.gst.toFixed(2) : ''; if (plan.gst != null) tag('gst', plan.checks.gst); }
    if (plan.total != null) { n.total.i.value = plan.total.toFixed(2); tag('total', plan.checks.total); } else tag('total', true);
    if (plan.date) { n.date.i.value = plan.date; tag('date', plan.checks.date); } else tag('date', true);
    if (plan.supplier) { n.sup.i.value = plan.supplier; tag('supplier', plan.checks.supplier); } else tag('supplier', true);
    st.rate = plan.vatRate; if (!plan.noVatNumber) tag('vat', plan.checks.vat);
    if (plan.category && st.categoryFrom !== 'person') { st.category = plan.category; st.categoryFrom = 'read'; }
    if (plan.whatFor && !n.what.i.value.trim()) { n.what.i.value = plan.whatFor; st.whatForSuggested = true; }
  }

  // ── Draw the state ─────────────────────────────────────────────────────────
  function setTag(t, kind, text) {
    t.hidden = !kind;
    t.className = `ax-tag${kind === 'check' ? ' ax-tag-check' : kind === 'sugg' ? ' ax-tag-sugg' : kind ? ' ax-tag-from' : ''}`;
    t.textContent = kind === 'check' ? 'Check' : text || (kind ? 'From the receipt' : '');
  }
  function paint() {
    if (!n.ax) return;
    const v = reader.view();
    if (v.phase === 'read' && v.result) applyRead(v.result);
    const reading = v.locked;
    const hasFile = !!v.file;
    n.ax.classList.toggle('reading', reading);
    card.classList.toggle('ax-is-reading', reading);
    n.drop.hidden = hasFile;
    n.rec.hidden = !hasFile;
    n.drop.classList.toggle('ax-drop-none', v.noReceipt);
    n.noRec.textContent = v.noReceipt ? 'Add a receipt instead' : 'No receipt';
    n.noRec.hidden = hasFile;
    if (hasFile) {
      if (st.image) { if (n.img.src !== st.image.url) n.img.src = st.image.url; n.img.hidden = false; } else { n.img.removeAttribute('src'); n.img.hidden = true; }
      n.fName.textContent = v.file.file.name || 'Receipt';
      n.fSize.textContent = ` · ${sizeLabel(v.file.file.size || 0)}`;
      n.fSpin.hidden = !reading; n.replace.hidden = reading;
    }
    n.rec.classList.toggle('reading', reading);

    // Lock while reading: nothing can be typed or saved.
    n.ax.querySelectorAll('.ax-right input, .ax-right select, .ax-right button').forEach((x) => { x.disabled = reading; });

    // The note at the top, or the plain message.
    n.hint.hidden = hasFile || v.noReceipt;
    let warnText = null;
    if (v.phase === 'failed') warnText = [null, 'Couldn’t read this receipt. Fill it in below; it’s still attached.'];
    else if (v.noReceipt) warnText = ['No receipt.', ' Fill it in by hand; it reaches your approver marked “No receipt”, with no VAT to reclaim.'];
    else if (st.plan && st.plan.noVatNumber && st.rate === 0 && st.currency !== INR) {
      const t = parseMoney(n.total.i.value);
      warnText = ['No VAT number on this receipt,', ` so the VAT can’t be claimed back. It’s saved as ${t ? gbp(t) : 'the total'} with no VAT. Ask the shop for a VAT receipt and drop it in to put it right.`];
    } else if (v.phase === 'manual' && hasFile && !readerEnabled()) warnText = [null, 'Fill in the figures from the receipt; it’s attached and files with the claim.'];
    n.warn.hidden = !warnText;
    n.warn.classList.toggle('ax-warn-soft', !!warnText && v.phase === 'manual' && !v.noReceipt);
    if (warnText) {
      const ico = el('span', 'ax-warn-ico'); ico.innerHTML = ICON_WARN;
      const t = el('span');
      if (warnText[0]) t.appendChild(el('b', null, warnText[0]));
      t.append(warnText[1]);
      n.warn.replaceChildren(ico, t);
    }

    // Fields + tags.
    setTag(n.total.t, st.tags.total);
    setTag(n.date.t, st.tags.date);
    setTag(n.sup.t, st.tags.supplier);
    setTag(n.what.t, st.whatForSuggested ? 'sugg' : null, 'Suggested');
    n.seg.querySelectorAll('[data-rate]').forEach((b) => { const on = Number(b.dataset.rate) === st.rate; b.classList.toggle('on', on); b.setAttribute('aria-pressed', String(on)); });
    const total = parseMoney(n.total.i.value);
    const inr = st.currency === INR;
    // The amount's sign follows the currency; in rupees VAT becomes GST (optional).
    const aff = n.total.b.querySelector('.ax-aff'); if (aff) aff.textContent = SYMBOL[st.currency];
    n.fVat.querySelector('.tf-label').textContent = inr ? 'GST (optional)' : 'VAT';
    n.vatRow.hidden = inr; n.gstRow.hidden = !inr;
    n.approx.textContent = inr ? approxGbpText(total, INR, st.fx) : '';
    n.cur.disable(reading);
    if (inr) {
      const g = parseMoney(n.gst.i.value);
      let sp = null; try { sp = total ? gstSplit(total, g) : null; } catch (_) { sp = null; }
      n.gstSplitText.textContent = !total ? 'What the bill shows as GST, if it shows any' : !sp ? 'The GST is more than the total' : sp.gst ? `GST ${money(sp.gst)} · net ${money(sp.net)} · not UK VAT` : `${money(sp.gross)} all in · no GST entered`;
    }
    const s = total ? vatFromGross(total, st.rate) : null;
    n.split.textContent = !s ? 'The split shows once there’s a total' : s.rate ? `VAT ${gbp(s.vat)} · net ${gbp(s.net)}` : `Nothing to reclaim · ${gbp(s.gross)} all in`;
    setTag(n.vatTag, s && st.tags.vat ? st.tags.vat : null);

    // Category chips: the five, then whatever else is chosen, then More…
    const list = [...QUICK_CATEGORIES];
    if (st.category && !list.includes(st.category)) list.push(st.category);
    n.chips.replaceChildren(...list.map((c) => {
      const b = el('button', `ax-chip${st.category === c ? ' on' : ''}`); b.type = 'button'; b.dataset.cat = c;
      b.setAttribute('aria-pressed', String(st.category === c));
      if (st.category === c && st.categoryFrom && st.categoryFrom !== 'person') { const i = el('span', 'ax-chip-spark'); i.innerHTML = ICON_SPARK; b.appendChild(i); }
      b.append(chipLabel(c));
      b.disabled = reading;
      return b;
    }), (() => { const m = el('button', 'ax-chip ax-chip-more', 'More…'); m.type = 'button'; m.dataset.more = '1'; m.disabled = reading; return m; })());
    n.catNote.textContent = st.categoryFrom === 'read' ? 'suggested from the items' : st.categoryFrom === 'suggested' ? 'suggested for project time — change it if it’s something else' : '';

    // Phone summary beside the thumbnail.
    n.sumTotal.textContent = total ? money(total) : '';
    n.sumSup.textContent = n.sup.i.value.trim();

    // Footer: "Reading the receipt · Fill it in yourself" while it reads; the button says what it will save.
    if (noteEl && !noteEl.classList.contains('tf-note-err')) {
      if (reading) {
        const fy = el('button', 'mi-link ax-fill-yourself', 'Fill it in yourself'); fy.type = 'button';
        fy.addEventListener('click', () => { reader.fillYourself(); n.total.i.focus(); });
        const w = footNote('Reading the receipt · ');
        w.appendChild(fy);
        noteEl.replaceChildren(w);
      } else noteEl.replaceChildren(document.createTextNode(filingFor(st.currency)));
    }
    if (primaryBtn && !primaryBtn.classList.contains('tf-working')) {
      const label = total ? `Add expense · ${money(total)}` : 'Add expense';
      primaryBtn.textContent = label; primaryBtn.dataset.verb = label;
      const ok = !reading && !!total;
      primaryBtn.disabled = !ok; primaryBtn.classList.toggle('tf-notyet', !ok);
    } else if (primaryBtn) primaryBtn.dataset.verb = total ? `Add expense · ${money(total)}` : 'Add expense';
    if (secondaryBtn && !secondaryBtn.classList.contains('tf-working')) secondaryBtn.disabled = reading || !total;
  }

  // ── Save ───────────────────────────────────────────────────────────────────
  async function save() {
    const v = reader.view();
    if (v.locked) throw new Error('Wait for the receipt to be read, or fill it in yourself.');
    const total = parseMoney(n.total.i.value);
    if (!total) throw new Error('Enter the total paid.');
    const date = n.date.i.value;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) throw new Error('Enter the date on the receipt.');
    if (date > isoToday()) throw new Error('The date is in the future. Check it against the receipt.');
    if (!st.category) throw new Error('Choose a category.');
    const inr = st.currency === INR;
    // Rupees: the GST the bill prints (optional), never UK VAT (vat stays null).
    const g = inr ? gstSplit(total, parseMoney(n.gst.i.value)) : null;
    const s = inr ? { net: g.net, vat: null, rate: null, gross: g.gross } : vatFromGross(total, st.rate);
    const supplier = n.sup.i.value.trim().slice(0, 120) || null;
    const description = n.what.i.value.trim().slice(0, 200) || null;
    const file = v.file && v.file.file;

    // Receipt filing — unchanged (before the doc, so the link can be stored).
    let receipt = null;
    if (file && contractor) {
      const { uploadReceipt } = await import('../data/receipts.js');
      receipt = await uploadReceipt(file, session);
    } else if (file) {
      receipt = await callFunction('fileReceipt', { data: await fileBase64(file), mimeType: fileType(file), fileName: file.name, category: st.category, date, ...(inr ? { currency: INR } : {}) });
    }

    const poId = n.po.value || null;
    const po = poId ? st.poById[poId] : null;
    const pid = n.project.value() || null;
    const p = pid ? (st.projects.find((x) => x.id === pid) || (project && (project.projectId || project.id) === pid ? project : null)) : null;
    const fromRead = !!(st.plan && st.readId);
    await addExpense({
      userEmail: session.email, ...ownerFields(session), userName: session.displayName,
      date, type: 'Expense',
      miles: null, amount: s.net, vat: s.vat || null, vatRate: s.rate, gross: s.gross, category: st.category,
      ...(inr ? { currency: INR, gst: g.gst } : {}),
      description, supplier, receipt,
      poId, poRef: po ? po.ref || null : null,
      projectId: pid, projectNo: p ? p.number || null : null,
      // New, all optional (Karan 26/10/2026; schema change log):
      entry: fromRead ? 'receipt' : 'manual',
      receiptRead: fromRead ? { id: st.readId, changed: changedFields(st.plan, { total, date, supplier, vatRate: s.rate, category: st.category, whatFor: description, currency: st.currency, ...(inr ? { gst: g.gst } : {}) }) } : null,
      noReceipt: !file,
      status: 'requested', createdAt: new Date().toISOString(),
    });
    reader.dispose();
  }

  return dlg;
}
